// Shared utility functions used across multiple components

// --- Date helpers ---

export function ymd(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// weekday: "long" → "Monday, March 9" (used on home pages)
export function formatEventDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  } catch {
    return dateStr;
  }
}

// weekday: "short" → "Mon, Mar 9" (used in modals and compact views)
export function formatEventDateShort(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return dateStr;
  }
}

// --- Time helpers ---

export function formatTime12(value) {
  if (!value) return "";
  const parts = String(value).split(":");
  if (parts.length === 0) return String(value);
  const h = Number(parts[0]);
  if (Number.isNaN(h)) return String(value);
  const m = (parts[1] ?? "00").slice(0, 2).padStart(2, "0");
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${m} ${suffix}`;
}

export function getPracticeStart(practice) {
  return practice?.startTime || practice?.time || null;
}

export function getPracticeEnd(practice) {
  return practice?.endTime || null;
}

export function formatPracticeTimeRange(practice) {
  const start = getPracticeStart(practice);
  const end = getPracticeEnd(practice);
  if (start && end && end !== start) {
    return `${formatTime12(start)} – ${formatTime12(end)}`;
  }
  if (start) return formatTime12(start);
  return "";
}

// --- RSVP helpers ---

export function getRsvpLabel(status, fallback = status) {
  const labels = {
    "Available": "✓ Available",
    "Not Available": "✗ Not Available",
    // Legacy support
    present: "✓ Attending",
    absent: "✗ Not Attending",
    maybe: "? Maybe",
    yes: "✓ Attending",
    no: "✗ Not Attending",
  };
  return labels[status] ?? fallback;
}

// --- Roster helpers ---

export const canonicalRole = (record) => {
  if (!record) return "";
  const raw = record.role_key || record.role;
  if (!raw) return "";
  const normalized = String(raw).trim().toLowerCase();
  if (normalized === "coach" || normalized === "athlete" || normalized === "parent") return normalized;
  return "";
};

export const uniqueFieldValues = (records, field) => {
  const m = new Map();
  records.forEach((record) => {
    const raw = (record[field] || "").trim();
    if (!raw) return;
    const key = raw.toLowerCase();
    if (!m.has(key)) m.set(key, raw);
  });
  return Array.from(m.values()).sort((a, b) => a.localeCompare(b));
};

export const getUserStatus = (user) => {
  if (!user.is_active) return { type: "inactive", label: "Inactive" };
  if (!user.login_completed) {
    if (user.has_invite) return { type: "invited", label: "Invited" };
    return { type: "not-invited", label: "Not Invited" };
  }
  return { type: "active", label: "Active" };
};
